import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './resources-dialog.css';
import RelationalDatabase from './relational-database/RelationalDatabase';
import Button from '../../lib/button/Button';
import Tooltip from '../../lib/tooltip/Tooltip';
import HttpAPI from './http-api/HttpAPI';
import ContainerComputeViewer from './container-compute/ContainerComputeViewer';
import SharedDriveViewer from './shared-drive/SharedDriveViewer';
import { getAccessToken } from '../../auth';
import { download } from '../../utils';
import { Route, Routes, useLocation, useParams } from 'react-router-dom';
import { EditorService } from '../../services/editor';
import DialogLoader from '../DialogLoader';
import { InfrastructureResourceType } from '@elyx-code/definitions';
import {
	resolveActionDescriptorNameById,
	toSentenceCase
} from '@elyx-code/project-logic-tree';
import { IDropdownMenuCardOption } from '../../lib/dropdown/DropdownMenuCard';
import { MenuItem, Select } from '@mui/material';
import { popupNotification } from '../../popup-notification';
import { Logger } from '@elyx-code/common-ts-utils';
import { envConfig } from '../../config';
import CloudLogsViewer from './logs/CloudLogsViewer';
import CronJobsList from './cron-job/CronJobsList';
import FunctionsList from './functions/FunctionsList';
import CloudFileSystemBucketExplorer from './cloud-file-system-bucket/CloudFileSystemBucketExplorer';
import FileViewer from './cloud-file-system-bucket/FileViewer';
import RelationalDatabaseIcon from '../../assets/relational-database.png';
import CloudFileSystemIcon from '../../assets/cloud-file-system.svg';
import HTTPEndpointIcon from '../../assets/http-endpoint.svg';
import CronJobIcon from '../../assets/cron-job.png';
import ContainerComputeIcon from '../../assets/container-compute.svg';
import SharedDriveIcon from '../../assets/shared-drive.svg';
import ExecutionIcon from '../../assets/execution.svg';
import TerminalIcon from '../../assets/terminal.svg';

export interface IResourcesDropdownOption extends IDropdownMenuCardOption {
	isSubItem?: boolean;
	iconFile?: string;
}

export interface IResourcesDialogProps {
	onClose: () => void;
	project: EditorService;
}

const ResourcesDialog: React.FC<IResourcesDialogProps> = ({
	onClose,
	project
}) => {
	const [isDownloadingDatabase, setIsDownloadingDatabase] =
		useState<boolean>(false);
	const { id: projectId } = useParams<{
		id: string;
	}>();
	const location = useLocation();
	const [liveResourcesOptions, setLiveResourcesOptions] = useState<
		IResourcesDropdownOption[]
	>([]);

	const useForceUpdate = () => {
		const [_, setValue] = useState(0);
		return () => setValue((value) => value + 1);
	};
	const forceUpdate = useForceUpdate(); // Update the component

	const pathSegments = location.pathname.split('/');
	const resourcesIdx = pathSegments.indexOf('resources');
	const resourceType =
		resourcesIdx !== -1 ? pathSegments[resourcesIdx + 1] : undefined;
	Logger.log('Is this present? : ', resourceType);

	let dropdownValue = resourceType;
	let resourceId: string | null = null;

	if (
		resourceType === InfrastructureResourceType.RelationalDatabase ||
		resourceType === InfrastructureResourceType.CloudFileSystemBucket ||
		resourceType === (InfrastructureResourceType as any).ContainerCompute ||
		resourceType === (InfrastructureResourceType as any).SharedDrive ||
		resourceType === 'terminal'
	) {
		resourceId =
			resourcesIdx !== -1 ? pathSegments[resourcesIdx + 2] : null;
		dropdownValue = resourceId;
	} else if (resourceType === 'logs') {
		dropdownValue =
			resourcesIdx !== -1 ? pathSegments[resourcesIdx + 2] : null;
	}

	const downloadDatabaseData = async () => {
		setIsDownloadingDatabase(true);

		try {
			const response = await axios.get(
				`${envConfig.API_BASE_URL}/relational-db-service/v1/${projectId}/presign-export/${resourceId}/all`,
				{
					headers: {
						Authorization: `Bearer ${await getAccessToken()}`
					}
				}
			);

			await download(
				response.data.presignedUrl,
				`database-${new Date().toISOString()}.zip`
			);
		} catch (error) {
			Logger.error('Error downloading database data:', error);
			popupNotification({
				text: 'Error downloading database data. Please try again later.',
				color: 'error'
			});
		}

		setIsDownloadingDatabase(false);
	};

	useEffect(() => {
		project?.on('live-resources-loaded', () => {
			forceUpdate();
		});

		if (!project?.liveResources) {
			return;
		}

		// Feature flag: allow only one HttpApi option (set to false to allow multiple in future)
		const allowOnlyOneHttpApiOption = true;

		const displayableResourceTypes = [
			InfrastructureResourceType.RelationalDatabase,
			InfrastructureResourceType.CloudFileSystemBucket,
			InfrastructureResourceType.HttpApi,
			InfrastructureResourceType.CronJob,
			InfrastructureResourceType.ServerlessFunction,
			InfrastructureResourceType.ContainerCompute,
			InfrastructureResourceType.SharedDrive
		] as const;

		const pathSegments = location.pathname.split('/');
		const resourcesIdx = pathSegments.indexOf('resources');
		const isLogsPage =
			resourcesIdx !== -1 && pathSegments[resourcesIdx + 1] === 'logs';
		const logFunctionName =
			isLogsPage && resourcesIdx !== -1
				? pathSegments[resourcesIdx + 2]
				: null;

		let matchingResource: any = null;
		if (logFunctionName) {
			matchingResource = project.liveResources.find((r) => {
				const details = (r.details as any) || {};
				return details.functionName === logFunctionName;
			});
		}

		let httpApiOptionAdded = false;

		const options = displayableResourceTypes.flatMap((type) => {
			const resourcesOfType = project.liveResources.filter(
				(r) => r.resourceType === type
			);

			if (resourcesOfType.length > 0) {
				if (
					type === InfrastructureResourceType.HttpApi &&
					allowOnlyOneHttpApiOption
				) {
					if (httpApiOptionAdded) return [];
					httpApiOptionAdded = true;
					const categoryOption: IResourcesDropdownOption = {
						value: type,
						label: `Endpoints`,
						iconFile: HTTPEndpointIcon,
						iconEnd: 'fa-solid fa-arrow-right-arrow-left',
						disabled: false,
						navigateTo: `${project?.editorBasePath}/resources/${type}`
					};
					if (logFunctionName) {
						const isHttpEndpoint = project.liveResources.some(
							(r) =>
								r.resourceType ===
									InfrastructureResourceType.HttpEndpoint &&
								(r.details as any)?.functionName ===
									logFunctionName
						);
						if (isHttpEndpoint) {
							const endpointRes = project.liveResources.find(
								(r) =>
									r.resourceType ===
										InfrastructureResourceType.HttpEndpoint &&
									(r.details as any)?.functionName ===
										logFunctionName
							);
							const rawName = endpointRes?.name || '';
							const readableName =
								resolveActionDescriptorNameById(rawName) ||
								toSentenceCase(rawName) ||
								logFunctionName;
							return [
								categoryOption,
								{
									value: logFunctionName,
									label: `Logs: ${readableName}`,
									iconFile: TerminalIcon,
									iconEnd: 'fa-solid fa-terminal',
									disabled: false,
									navigateTo: `${project?.editorBasePath}/resources/logs/${logFunctionName}`,
									isSubItem: true
								}
							];
						}
					}
					return [categoryOption];
				}
				if (type === InfrastructureResourceType.CronJob) {
					const categoryOption: IResourcesDropdownOption = {
						value: type,
						label: `Scheduled executions`,
						iconFile: CronJobIcon,
						iconEnd: 'fa-solid fa-clock',
						disabled: false,
						navigateTo: `${project?.editorBasePath}/resources/${type}`
					};
					if (
						matchingResource &&
						matchingResource.resourceType ===
							InfrastructureResourceType.CronJob
					) {
						const rawName = matchingResource.name || '';
						const readableName =
							resolveActionDescriptorNameById(rawName) ||
							toSentenceCase(rawName) ||
							logFunctionName;
						return [
							categoryOption,
							{
								value: logFunctionName,
								label: `Logs: ${readableName}`,
								iconFile: TerminalIcon,
								iconEnd: 'fa-solid fa-terminal',
								disabled: false,
								navigateTo: `${project?.editorBasePath}/resources/logs/${logFunctionName}`,
								isSubItem: true
							}
						];
					}
					return [categoryOption];
				}
				if (type === InfrastructureResourceType.ServerlessFunction) {
					const categoryOption: IResourcesDropdownOption = {
						value: type,
						label: `Serverless Functions`,
						iconFile: ExecutionIcon,
						iconEnd: 'fa-solid fa-bolt',
						disabled: false,
						navigateTo: `${project?.editorBasePath}/resources/${type}`
					};
					if (
						matchingResource &&
						matchingResource.resourceType ===
							InfrastructureResourceType.ServerlessFunction
					) {
						const rawName = matchingResource.name || '';
						const readableName =
							resolveActionDescriptorNameById(rawName) ||
							toSentenceCase(rawName) ||
							logFunctionName;
						return [
							categoryOption,
							{
								value: logFunctionName,
								label: `Logs: ${readableName}`,
								iconFile: TerminalIcon,
								iconEnd: 'fa-solid fa-terminal',
								disabled: false,
								navigateTo: `${project?.editorBasePath}/resources/logs/${logFunctionName}`,
								isSubItem: true
							}
						];
					}
					return [categoryOption];
				}
				// All RelationalDatabase resources are mapped
				return resourcesOfType
					.map<IResourcesDropdownOption>((res) => {
						switch (type) {
							case InfrastructureResourceType.RelationalDatabase:
								return {
									value: res.resourceId,
									label: `Database: ${res.name || res.resourceId}`,
									iconFile: RelationalDatabaseIcon,
									iconEnd: 'fa-solid fa-database',
									disabled: false,
									navigateTo: `${project?.editorBasePath}/resources/${type}/${res.resourceId}`
								};
							case InfrastructureResourceType.CloudFileSystemBucket:
								return {
									value: res.resourceId,
									label: `Bucket: ${res.name || res.resourceId}`,
									iconFile: CloudFileSystemIcon,
									iconEnd: 'fa-solid fa-folder-open',
									disabled: false,
									navigateTo: `${project?.editorBasePath}/resources/${type}/${res.resourceId}`
								};
							case (InfrastructureResourceType as any)
								.ContainerCompute:
								return {
									value: res.resourceId,
									label: `Compute: ${res.name || res.resourceId}`,
									iconFile: ContainerComputeIcon,
									iconEnd: 'fa-solid fa-server',
									disabled: false,
									navigateTo: `${project?.editorBasePath}/resources/${type}/${res.resourceId}`
								};
							case (InfrastructureResourceType as any)
								.SharedDrive:
								return {
									value: res.resourceId,
									label: `Drive: ${res.name || res.resourceId}`,
									iconFile: SharedDriveIcon,
									iconEnd: 'fa-solid fa-hard-drive',
									disabled: false,
									navigateTo: `${project?.editorBasePath}/resources/${type}/${res.resourceId}`
								};
							case InfrastructureResourceType.CloudFileSystemBucket:
								return {
									value: res.resourceId,
									label: `Bucket: ${res.name}`,
									iconEnd: 'fa-solid fa-folder-open',
									disabled: false,
									navigateTo: `${project?.editorBasePath}/resources/${type}/${res.resourceId}`
								};
							case InfrastructureResourceType.CloudFileSystemBucket:
								return {
									value: res.resourceId,
									label: `Bucket: ${res.name}`,
									iconEnd: 'fa-solid fa-folder-open',
									disabled: false,
									navigateTo: `${project?.editorBasePath}/resources/${type}/${res.resourceId}`
								};
							default:
								return null as any;
						}
					})
					.filter(Boolean);
			} else {
				let label = '';
				let iconFile = '';
				let iconEnd = '';
				let disabled = false;
				let navigateTo = '';
				let tooltip = '';

				switch (type) {
					case InfrastructureResourceType.RelationalDatabase:
						label = 'Database';
						iconFile = RelationalDatabaseIcon;
						iconEnd = 'fa-solid fa-database';
						disabled = true;
						navigateTo = `${project?.editorBasePath}/resources/${type}`;
						tooltip =
							'No database exist in this published version of the project';
						break;
					case InfrastructureResourceType.CloudFileSystemBucket:
						label = 'Cloud Buckets';
						iconFile = CloudFileSystemIcon;
						iconEnd = 'fa-solid fa-folder-open';
						disabled = true;
						navigateTo = `${project?.editorBasePath}/resources/${type}`;
						tooltip =
							'No buckets exist in this published version of the project';
						break;
					case InfrastructureResourceType.HttpApi:
						label = 'Endpoints';
						iconFile = HTTPEndpointIcon;
						iconEnd = 'fa-solid fa-arrow-right-arrow-left';
						disabled = true;
						navigateTo = `${project?.editorBasePath}/resources/${type}`;
						tooltip =
							'No endpoints exist in this published version of the project';
						break;
					case InfrastructureResourceType.CronJob:
						label = 'Scheduled executions';
						iconFile = CronJobIcon;
						iconEnd = 'fa-solid fa-clock';
						disabled = true;
						navigateTo = `${project?.editorBasePath}/resources/${type}`;
						tooltip =
							'No scheduled executions exist in this published version of the project';
						break;
					case InfrastructureResourceType.ServerlessFunction:
						label = 'Serverless Functions';
						iconFile = ExecutionIcon;
						iconEnd = 'fa-solid fa-bolt';
						disabled = true;
						navigateTo = `${project?.editorBasePath}/resources/${type}`;
						tooltip =
							'No functions exist in this published version of the project';
						break;
					case (InfrastructureResourceType as any).ContainerCompute:
						label = 'Container Compute';
						iconFile = ContainerComputeIcon;
						iconEnd = 'fa-solid fa-server';
						disabled = true;
						navigateTo = `${project?.editorBasePath}/resources/${type}`;
						tooltip =
							'No container instances exist in this published version of the project';
						break;
					case (InfrastructureResourceType as any).SharedDrive:
						label = 'Shared Drives';
						iconFile = SharedDriveIcon;
						iconEnd = 'fa-solid fa-hard-drive';
						disabled = true;
						navigateTo = `${project?.editorBasePath}/resources/${type}`;
						tooltip =
							'No shared drives exist in this published version of the project';
						break;
				}

				// no resources of this type → show a disabled placeholder
				return [
					{
						value: type,
						label: label,
						iconFile: iconFile,
						iconEnd: iconEnd,
						disabled: disabled,
						tooltip: tooltip
					}
				];
			}
		});

		setLiveResourcesOptions(options);
	}, [project?.liveResources, projectId, location.pathname]);

	useEffect(() => {
		const currentProjectId = project?.projectId || projectId;
		if (!project?.userSettings || !currentProjectId) return;
		const resourcesIdx = location.pathname.indexOf('/resources');
		if (resourcesIdx !== -1) {
			let resourcePath = location.pathname.substring(resourcesIdx);
			if (resourcePath.endsWith('/') && resourcePath !== '/resources/') {
				resourcePath = resourcePath.slice(0, -1);
			}
			if (
				resourcePath &&
				resourcePath !== '/resources' &&
				resourcePath !== '/resources/'
			) {
				const currentMap =
					project.userSettings.lastOpenInfraResourcePathByProject ||
					{};
				if (currentMap[currentProjectId] !== resourcePath) {
					project.userSettings.set(
						'lastOpenInfraResourcePathByProject',
						{
							...currentMap,
							[currentProjectId]: resourcePath
						}
					);
				}
			}
		}
	}, [
		location.pathname,
		project?.userSettings,
		project?.projectId,
		projectId
	]);

	// Once the resources have loaded, if the URL is on a resource-type root
	// (e.g. `/resources/relational-database` with no specific resource id)
	// auto-navigate to the first available resource of that type so the user
	// doesn't have to pick from the dropdown manually.
	useEffect(() => {
		if (!liveResourcesOptions.length) return;

		const editorBasePath = project?.editorBasePath || '';
		const currentProjectId = project?.projectId || projectId;

		const pathSegments = location.pathname.split('/');
		const resourcesIdx = pathSegments.indexOf('resources');
		const resourceTypeFromUrl =
			resourcesIdx !== -1 && pathSegments.length > resourcesIdx + 1
				? pathSegments[resourcesIdx + 1]
				: undefined;
		const resourceIdFromUrl =
			resourcesIdx !== -1 && pathSegments.length > resourcesIdx + 2
				? pathSegments[resourcesIdx + 2]
				: undefined;

		const findMatchingOptionForPath = (relPath: string) => {
			return liveResourcesOptions.find((opt) => {
				if (opt.disabled || !opt.navigateTo) return false;
				const optRelPath = opt.navigateTo.substring(
					opt.navigateTo.indexOf('/resources')
				);
				return (
					optRelPath === relPath ||
					relPath.startsWith(optRelPath + '/') ||
					optRelPath.startsWith(relPath + '/')
				);
			});
		};

		// 1. If URL has a resourceType specified (e.g. `/resources/relational-database` or `/resources/http-api`)
		if (resourceTypeFromUrl) {
			// If URL already has a resourceId or subpath (e.g. `/resources/relational-database/res-123` or `/resources/http-api/test` or `/resources/logs/func`),
			// no auto-redirection is needed as the user is on a specific view.
			if (resourceIdFromUrl) return;

			// If URL is at a resourceType root (e.g. `/resources/relational-database` or `/resources/http-api`),
			// check if current URL already matches an enabled option (like `/resources/http-api`).
			const relPathFromUrl = location.pathname.substring(
				location.pathname.indexOf('/resources')
			);
			const exactMatch = liveResourcesOptions.find(
				(opt) =>
					!opt.disabled &&
					opt.navigateTo &&
					(opt.navigateTo.endsWith(relPathFromUrl) ||
						opt.navigateTo.includes(relPathFromUrl))
			);
			if (exactMatch) return; // User is already on a valid root resource page like `/resources/http-api`

			// Otherwise (e.g. `/resources/relational-database` where options have IDs like `/resources/relational-database/res-123`),
			// auto-navigate to the first available selectable option of that resource type.
			const firstSelectable = liveResourcesOptions.find(
				(option) =>
					!option.disabled &&
					!!option.navigateTo &&
					(option.navigateTo.includes(
						`/resources/${resourceTypeFromUrl}/`
					) ||
						option.navigateTo.endsWith(
							`/resources/${resourceTypeFromUrl}`
						))
			);

			if (firstSelectable?.navigateTo) {
				EditorService.navigate(firstSelectable.navigateTo);
			}
			return;
		}

		// 2. If URL is on `/resources` (or `/resources/`), try restoring stored last path from userSettings
		const storedMap =
			project?.userSettings?.lastOpenInfraResourcePathByProject;
		const storedPath =
			currentProjectId && storedMap
				? storedMap[currentProjectId]
				: undefined;

		if (
			storedPath &&
			storedPath !== '/resources' &&
			storedPath !== '/resources/'
		) {
			const matchingOption = findMatchingOptionForPath(storedPath);

			if (matchingOption) {
				const targetUrl = `${editorBasePath}${storedPath}`;
				if (location.pathname !== targetUrl) {
					EditorService.navigate(targetUrl);
				}
				return;
			}
		}

		// 3. Priority order fallback if no valid stored path exists
		const priorityPrefixes = [
			'/resources/http-api',
			'/resources/relational-database',
			`/resources/${InfrastructureResourceType.ContainerCompute}`,
			'/resources/cron-job',
			'/resources/serverless-function',
			`/resources/${(InfrastructureResourceType as any).SharedDrive}`
		];

		for (const prefix of priorityPrefixes) {
			const selectable = liveResourcesOptions.find((opt) => {
				if (opt.disabled || !opt.navigateTo) return false;
				const optRelPath = opt.navigateTo.substring(
					opt.navigateTo.indexOf('/resources')
				);
				return optRelPath.startsWith(prefix);
			});
			if (selectable?.navigateTo) {
				EditorService.navigate(selectable.navigateTo);
				return;
			}
		}

		// 4. Ultimate fallback to any enabled option
		const fallback = liveResourcesOptions.find(
			(opt) => !opt.disabled && !!opt.navigateTo
		);
		if (fallback?.navigateTo) {
			EditorService.navigate(fallback.navigateTo);
		}
	}, [
		liveResourcesOptions,
		location.pathname,
		project?.userSettings,
		project?.projectId,
		project?.editorBasePath,
		projectId
	]);

	const isRelationalDatabase = location.pathname.includes(
		InfrastructureResourceType.RelationalDatabase
	);

	// const isContainerCompute =
	// 	location.pathname.includes(
	// 		(InfrastructureResourceType as any).ContainerCompute
	// 	) || location.pathname.includes('/resources/terminal');

	const isActiveDatabase =
		resourceType === InfrastructureResourceType.RelationalDatabase &&
		resourceId !== null &&
		resourceId !== undefined;

	if (!project?.liveResources) {
		return (
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					height: '100%'
					// marginBottom: isRelationalDatabase ? '28px' : '',
				}}
			>
				<DialogLoader
					title="Loading resources"
					subtitle="Please wait while we load your resources."
					loadingMessage="Loading resources..."
					onClose={onClose}
				/>
			</div>
		);
	}

	Logger.log('Dropdown value: ', dropdownValue);

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				marginBottom: isRelationalDatabase ? '28px' : '',
				height: '100%'
			}}
		>
			<div
				style={{
					padding: '40px',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					paddingBottom: '0px'
				}}
			>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						zIndex: 0,
						gap: '16px'
					}}
				>
					<button className="dialog_back_button" onClick={onClose}>
						<i className="fa-solid fa-arrow-left"></i>
					</button>
					<h1
						style={{
							margin: '0px',
							fontWeight: 500,
							fontSize: '28px',
							marginRight: '24px'
						}}
					>
						Assets
					</h1>
					<Select
						key={
							liveResourcesOptions.length > 0
								? `loaded-${dropdownValue ?? ''}`
								: 'loading'
						}
						value={
							liveResourcesOptions.some(
								(option) => option.value === dropdownValue
							)
								? (dropdownValue as string)
								: ''
						}
						size="small"
						onChange={(e) => {
							const selectedResource = liveResourcesOptions.find(
								(option) => option.value === e.target.value
							);
							if (selectedResource) {
								EditorService.navigate(
									selectedResource.navigateTo
								);
							}
						}}
						displayEmpty
						inputProps={{ 'aria-label': 'Without label' }}
						renderValue={(selected) => {
							if (!selected) {
								if (liveResourcesOptions.length === 0) {
									return (
										<span
											style={{
												display: 'flex',
												gap: '8px',
												alignItems: 'center',
												color: 'rgba(0, 0, 0, 0.54)'
											}}
										>
											<i
												className="fa-solid fa-spinner fa-spin"
												style={{
													fontSize: '12px',
													color: '#6366f1'
												}}
											/>
											Loading resources...
										</span>
									);
								}
								return <span>Select a resource</span>;
							}

							const selectedOption = liveResourcesOptions.find(
								(option) => option.value === selected
							);

							return (
								<span
									style={{
										display: 'flex',
										gap: '12px',
										alignItems: 'center'
									}}
								>
									{selectedOption?.iconFile ? (
										<img
											src={selectedOption.iconFile}
											alt=""
											style={{
												width: '18px',
												height: '18px',
												objectFit: 'contain'
											}}
										/>
									) : (
										<i
											className={selectedOption?.iconEnd}
										/>
									)}
									{selectedOption?.label}
								</span>
							);
						}}
					>
						{liveResourcesOptions.length === 0 ? (
							<MenuItem
								disabled
								style={{
									display: 'flex',
									gap: '12px',
									alignItems: 'center',
									justifyContent: 'center',
									padding: '16px',
									minWidth: '240px'
								}}
							>
								<i
									className="fa-solid fa-spinner fa-spin"
									style={{ color: '#6366f1' }}
								/>
								<span
									style={{ fontSize: '13px', color: '#666' }}
								>
									Loading live resources...
								</span>
							</MenuItem>
						) : (
							liveResourcesOptions.map((option) => {
								const itemStyle: React.CSSProperties = {
									display: 'flex',
									gap: '12px',
									alignItems: 'center'
								};
								if (option.isSubItem) {
									itemStyle.paddingLeft = '32px';
									itemStyle.color = '#666';
									itemStyle.fontSize = '12.5px';
								}
								return (
									<MenuItem
										key={option.value}
										value={option.value}
										disabled={option.disabled}
										style={itemStyle}
									>
										{option.isSubItem ? (
											<span
												style={{
													display: 'flex',
													alignItems: 'center',
													gap: '6px',
													color: '#888'
												}}
											>
												<span
													style={{
														color: '#cbd5e1',
														fontFamily: 'monospace',
														userSelect: 'none',
														fontWeight: 'bold'
													}}
												>
													└─
												</span>
												{option.iconFile ? (
													<img
														src={option.iconFile}
														alt=""
														style={{
															width: '16px',
															height: '16px',
															objectFit: 'contain'
														}}
													/>
												) : (
													<i
														className={
															option.iconEnd
														}
														style={{
															fontSize: '11px'
														}}
													/>
												)}
											</span>
										) : option.iconFile ? (
											<img
												src={option.iconFile}
												alt=""
												style={{
													width: '18px',
													height: '18px',
													objectFit: 'contain'
												}}
											/>
										) : (
											<i
												className={option.iconEnd}
												style={{ fontSize: '14px' }}
											/>
										)}
										{option.label}
									</MenuItem>
								);
							})
						)}
					</Select>
					<button
						onClick={() => project?.loadLiveResources()}
						style={{
							background: 'none',
							border: 'none',
							cursor: 'pointer',
							color: '#6366f1',
							padding: '8px',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							borderRadius: '50%',
							transition: 'background 0.2s'
						}}
						title="Refresh resources"
						onMouseEnter={(e) =>
							(e.currentTarget.style.backgroundColor =
								'rgba(99, 102, 241, 0.1)')
						}
						onMouseLeave={(e) =>
							(e.currentTarget.style.backgroundColor =
								'transparent')
						}
					>
						<i className="fa-solid fa-rotate"></i>
					</button>
				</div>

				{isActiveDatabase && (
					<span>
						<Button
							iconStart="fa-solid fa-download"
							variant="outlined"
							size="small"
							id="download-relational-db-data-button"
							loading={isDownloadingDatabase}
							onClick={downloadDatabaseData}
							disabled={!resourceId}
						>
							Export CSV
						</Button>
						<Tooltip
							anchorQuerySelector="#download-relational-db-data-button"
							placement="bottom"
						>
							Download all your database data as a CSV file.
						</Tooltip>
					</span>
				)}
			</div>
			<span
				style={{
					display: 'flex',
					gap: '6px',
					alignItems: 'center',
					marginLeft: '72px'
				}}
			>
				<div className="resource-dialog__live-indicator-dot blinking"></div>
				<p style={{ margin: '0px', color: 'rgba(0,0,0,0.7)' }}>
					Live resources
				</p>
			</span>

			{!dropdownValue && (
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						height: '100%'
					}}
				>
					<p
						style={{
							margin: '0px',
							color: 'rgba(0,0,0,0.54)',
							padding: '0px 40px 0 80px'
						}}
					>
						Select a resource to view its details.
					</p>
				</div>
			)}

			{/* <section> */}
			<Routes>
				<Route
					path={`${InfrastructureResourceType.HttpApi}/*`}
					element={<HttpAPI onClose={onClose} project={project} />}
				/>
				<Route
					path={`${InfrastructureResourceType.RelationalDatabase}/:resourceId`}
					element={
						<RelationalDatabase
							onClose={onClose}
							project={project}
						/>
					}
				/>
				<Route
					path={`${InfrastructureResourceType.CloudFileSystemBucket}/:resourceId/*`}
					element={
						<Routes>
							<Route
								path="view/*"
								element={
									<FileViewer
										project={project}
										resourceId={resourceId as string}
									/>
								}
							/>
							<Route
								path="*"
								element={
									<CloudFileSystemBucketExplorer
										onClose={onClose}
										project={project}
										resourceId={resourceId as string}
									/>
								}
							/>
						</Routes>
					}
				/>
				<Route
					path={InfrastructureResourceType.CronJob}
					element={
						<CronJobsList onClose={onClose} project={project} />
					}
				/>
				<Route
					path={InfrastructureResourceType.ServerlessFunction}
					element={
						<FunctionsList onClose={onClose} project={project} />
					}
				/>
				<Route
					path="logs/:functionName"
					element={
						<CloudLogsViewer
							onClose={onClose}
							project={project}
							projectId={projectId as string}
						/>
					}
				/>
				<Route
					path={`${InfrastructureResourceType.ContainerCompute}/:computeId`}
					element={
						<ContainerComputeViewer
							key={location.pathname}
							onClose={onClose}
							project={project}
							projectId={projectId as string}
						/>
					}
				/>
				<Route
					path={`${(InfrastructureResourceType as any).SharedDrive}/:resourceId`}
					element={
						<SharedDriveViewer
							onClose={onClose}
							project={project}
							projectId={projectId as string}
						/>
					}
				/>
			</Routes>
			{/* </section> */}
		</div>
	);
};

export default ResourcesDialog;
